/* eslint-disable import/no-cycle */
import {decorate, observable} from 'mobx';
import {Api} from '../middleware/api';
import {Workplace} from '../models/workplace';
import {EntityStore} from './entityStore';
import {RelevantDocument} from '../models/relevantDocument';
import {Document} from '../models/document';
import {loadLatestLogEndpoint} from '../middleware/endpoints/workplaces';

export class WorkplaceStore extends EntityStore {
  workplaces = [];
  selectedWorkplace = undefined;
  relevantDocuments = {
    hierarchyDocuments: [],
    materialDocuments: [],
    changeDocuments: [],
  };

  constructor(rootStore) {
    super(rootStore, 'workplaces', Api.workplaces, Workplace);
  }

  getDependencies() {
    return [
      {
        store: this.rootStore.workplaceGroupStore,
        modelId: 'groupId',
      },
      {
        store: this.rootStore.terminalLayoutsStore,
        modelId: 'terminalLayoutId',
      },
    ];
  }

  loadRelevantDocumentsToSelectedWorkplace() {
    Api.workplaces.loadRelevantDocuments(this.selectedWorkplace.id).then((documents) => {
      this.relevantDocuments.hierarchyDocuments = documents.data.hierarchyDocuments
        .map((document) => Document.fromPlainObject(document));
      this.relevantDocuments.hierarchyDocuments
        .forEach((document) => document.populateAttributesFromStore(this.rootStore));
      this.relevantDocuments.materialDocuments = documents.data.materialDocuments
        .map((document) => Document.fromPlainObject(document));
      this.relevantDocuments.materialDocuments
        .forEach((document) => document.populateAttributesFromStore(this.rootStore));
      this.relevantDocuments.changeDocuments = documents.data.changeDocuments
        .map((document) => Document.fromPlainObject(document));
      this.relevantDocuments.changeDocuments
        .forEach((document) => document.populateAttributesFromStore(this.rootStore));
    });
  }

  loadLatestLogByState(workplaceId, operationStateId, workplaceStateId) {
    this.api[loadLatestLogEndpoint]({workplaceId, operationStateId, workplaceStateId}).then(({data}) => {
      if (data.operationStateLog) {
        this.rootStore.operationStore.load(data.operationStateLog.operationId);
        this.rootStore.operationStateLogStore
          .add(this.rootStore.operationStateLogStore.createModelInstance(data.operationStateLog));
      }
      if (data.workplaceStateLog) {
        this.rootStore.workplaceStateLogStore
          .add(this.rootStore.workplaceStateLogStore.createModelInstance(data.workplaceStateLog));
      }
    });
  }

  getRelevantDocumentsWidgetTree(categoryEntries) {
    return RelevantDocument
      .getRelevantDocumentsTree(this.relevantDocuments, categoryEntries)
      .filter((node) => node.children.length > 0);
  }

  getByHierarchyId(hierarchyId) {
    return this.workplaces.find((w) => w.hierarchyId === hierarchyId);
  }

  get relevantDocumentsTree() {
    return RelevantDocument
      .getRelevantDocumentsTree(this.relevantDocuments)
      .filter((node) => node.children.length > 0);
  }

  getByNo(workplaceNo) {
    return this.workplaces.find((workplace) => workplace.no === workplaceNo);
  }

  async advancedSearch(params) {
    return this.api.search(params).then((response) => response.data);
  }

  performQuickstop({workplaceId, body}) {
    this.api
      .performQuickstop({id: workplaceId, ...body})
      .then((res) => {
        (res.data)
          ? this.addMessage({type: 'success', title: 'flashMessages.quickstopSuccess'}, {})
          : this.addMessage({type: 'error', title: 'flashMessages.quickstopError'}, {});
      })
      .catch((e) => {
        this.addMessage({type: 'error', title: `flashMessages.quickstopError: ${e.message}`}, {});
        this.handleApiError(e);
      });
  }

  getVirtualSiblingWorkplaces(workplaceId) {
    const virtualSiblingWorkplaces = [];
    const workplace = this.rootStore.workplaceStore.getById(workplaceId);
    if (workplace.hierarchyId) {
      const hierarchy = this.rootStore.hierarchyStore.getById(workplace.hierarchyId);
      const parentWorkplace = this.rootStore.workplaceStore.getByHierarchyId(hierarchy.parent?.id);
      if (parentWorkplace?.isVirtualRoot) {
        this.rootStore.hierarchyStore.getChildren(hierarchy.parent.id).forEach((child) => {
          const childWorkplace = this.rootStore.workplaceStore.getByHierarchyId(child.id);
          if (childWorkplace) {
            if (childWorkplace.id !== workplaceId) {
              virtualSiblingWorkplaces.push(childWorkplace);
            }
          }
        });
      }
    }

    return virtualSiblingWorkplaces;
  }
}

decorate(WorkplaceStore, {
  workplaces: observable,
  selectedWorkplace: observable,
  relevantDocuments: observable,
});
